import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Square, Pencil, Undo, Save, X, ZoomIn, ZoomOut, RotateCcw, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

export default function ManualRedactionEditor({ 
  isOpen, 
  onClose, 
  fileUrl, 
  fileName,
  fileIndex,
  accidentId,
  assessmentId,
  onSave 
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [tool, setTool] = useState('rectangle');
  const [isDrawing, setIsDrawing] = useState(false);
  const [redactionsByPage, setRedactionsByPage] = useState({});  // { "0": {rectangles: [], brushStrokes: []}, "1": {...} }
  const [currentRect, setCurrentRect] = useState(null);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [images, setImages] = useState([]);  // All page images
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotateLeft = () => {
    setRotation(prev => (prev - 90 + 360) % 360);
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const loadPageImage = (url, headers) => {
    return new Promise((resolve, reject) => {
      fetch(url, headers)
        .then(res => res.blob())
        .then(blob => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        })
        .catch(reject);
    });
  };

  const drawCanvas = (img, pageNum) => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    
    // Get redactions for current page
    const pageKey = String(pageNum);
    const pageRedactions = redactionsByPage[pageKey] || { rectangles: [], brushStrokes: [] };
    const rects = pageRedactions.rectangles || [];
    const strokes = pageRedactions.brushStrokes || [];
    
    // Calculate rotated dimensions
    const isRotated = rotation === 90 || rotation === 270;
    const displayWidth = isRotated ? img.height : img.width;
    const displayHeight = isRotated ? img.width : img.height;
    
    // Set canvas size to match rotated image
    canvas.width = displayWidth;
    canvas.height = displayHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Save context state
    ctx.save();
    
    // Apply rotation transformation
    if (rotation !== 0) {
      ctx.translate(displayWidth / 2, displayHeight / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-img.width / 2, -img.height / 2);
    }
    
    // Draw image
    ctx.drawImage(img, 0, 0);
    
    // Restore to apply redactions in rotated space
    ctx.restore();

    // Draw existing rectangles (in rotated coordinates)
    ctx.fillStyle = 'black';
    rects.forEach(rect => {
      const rotatedCoords = rotateCoordinates(rect.x, rect.y, rect.width, rect.height, img.width, img.height, rotation);
      ctx.fillRect(rotatedCoords.x, rotatedCoords.y, rotatedCoords.width, rotatedCoords.height);
    });

    // Draw existing brush strokes
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 20;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    strokes.forEach(stroke => {
      if (stroke.length < 2) return;
      ctx.beginPath();
      const startPoint = rotatePoint(stroke[0].x, stroke[0].y, img.width, img.height, rotation);
      ctx.moveTo(startPoint.x, startPoint.y);
      for (let i = 1; i < stroke.length; i++) {
        const point = rotatePoint(stroke[i].x, stroke[i].y, img.width, img.height, rotation);
        ctx.lineTo(point.x, point.y);
      }
      ctx.stroke();
    });
  };

  const rotatePoint = (x, y, imgWidth, imgHeight, angle) => {
    // Rotate point around image center
    if (angle === 0) return { x, y };
    
    const centerX = imgWidth / 2;
    const centerY = imgHeight / 2;
    const rad = (angle * Math.PI) / 180;
    
    const dx = x - centerX;
    const dy = y - centerY;
    
    const newX = dx * Math.cos(rad) - dy * Math.sin(rad) + (angle === 90 || angle === 270 ? imgHeight / 2 : centerX);
    const newY = dx * Math.sin(rad) + dy * Math.cos(rad) + (angle === 90 || angle === 270 ? imgWidth / 2 : centerY);
    
    return { x: newX, y: newY };
  };

  const rotateCoordinates = (x, y, w, h, imgWidth, imgHeight, angle) => {
    const topLeft = rotatePoint(x, y, imgWidth, imgHeight, angle);
    const bottomRight = rotatePoint(x + w, y + h, imgWidth, imgHeight, angle);
    
    return {
      x: Math.min(topLeft.x, bottomRight.x),
      y: Math.min(topLeft.y, bottomRight.y),
      width: Math.abs(bottomRight.x - topLeft.x),
      height: Math.abs(bottomRight.y - topLeft.y)
    };
  };

  const loadAllPages = async () => {
    try {
      setLoading(true);
      
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      
      // Determine endpoint base
      const baseUrl = accidentId 
        ? `${API}/accidents/${accidentId}/files/${fileIndex}`
        : `${API}/assessments/${assessmentId}/files/${fileIndex}`;
      
      // Get page count only
      const pageCountRes = await fetch(`${baseUrl}/pages`, headers);
      const pageData = await pageCountRes.json();
      const pageCount = pageData.pages || 1;
      
      setTotalPages(pageCount);
      console.log(`PDF has ${pageCount} page(s) - loading page 1...`);
      
      // Load ONLY first page initially (lazy load others)
      const firstPageImg = await loadPageImage(`${baseUrl}/image?page=0`, headers);
      
      // Initialize images array with nulls, set first page
      const imagesArray = new Array(pageCount).fill(null);
      imagesArray[0] = firstPageImg;
      
      setImages(imagesArray);
      setCurrentPage(0);
      setImageLoaded(true);
      setLoading(false);
      
      // Draw first page
      drawCanvas(firstPageImg, 0);
    } catch (error) {
      console.error('Failed to load pages:', error);
      toast.error('Failed to load file');
      setLoading(false);
    }
  };

  const loadPageIfNeeded = async (pageNum) => {
    // Check if page is already loaded
    if (images[pageNum]) {
      return images[pageNum];
    }
    
    // Load the page
    try {
      setLoading(true);
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      
      const baseUrl = accidentId 
        ? `${API}/accidents/${accidentId}/files/${fileIndex}`
        : `${API}/assessments/${assessmentId}/files/${fileIndex}`;
      
      console.log(`Loading page ${pageNum + 1}...`);
      const img = await loadPageImage(`${baseUrl}/image?page=${pageNum}`, headers);
      
      // Update images array
      const newImages = [...images];
      newImages[pageNum] = img;
      setImages(newImages);
      setLoading(false);
      
      return img;
    } catch (error) {
      console.error(`Failed to load page ${pageNum + 1}:`, error);
      toast.error(`Failed to load page ${pageNum + 1}`);
      setLoading(false);
      return null;
    }
  };

  useEffect(() => {
    if (isOpen && (accidentId || assessmentId) && fileIndex !== null) {
      loadAllPages();
    }
    
    // Add mouse wheel zoom
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isOpen, fileIndex, accidentId, assessmentId, zoom]);

  useEffect(() => {
    if (images[currentPage] && imageLoaded) {
      drawCanvas(images[currentPage], currentPage);
    }
  }, [rotation, currentPage]);

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / (rect.width / zoom);
    const scaleY = canvas.height / (rect.height / zoom);
    
    const displayX = (e.clientX - rect.left) / zoom;
    const displayY = (e.clientY - rect.top) / zoom;
    
    // Convert display coordinates back to original image coordinates (accounting for rotation)
    const img = images[currentPage];
    if (!img) return { x: 0, y: 0 };
    
    const imgWidth = img.width;
    const imgHeight = img.height;
    
    let originalX, originalY;
    
    if (rotation === 0) {
      originalX = displayX;
      originalY = displayY;
    } else if (rotation === 90) {
      originalX = displayY;
      originalY = imgHeight - displayX;
    } else if (rotation === 180) {
      originalX = imgWidth - displayX;
      originalY = imgHeight - displayY;
    } else { // 270
      originalX = imgWidth - displayY;
      originalY = displayX;
    }
    
    return { x: originalX, y: originalY };
  };

  const handleMouseDown = (e) => {
    const coords = getCanvasCoordinates(e);
    setIsDrawing(true);

    if (tool === 'rectangle') {
      setCurrentRect({ x: coords.x, y: coords.y, width: 0, height: 0 });
    } else if (tool === 'brush') {
      setCurrentStroke([coords]);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !images[currentPage]) return;

    const coords = getCanvasCoordinates(e);
    const img = images[currentPage];

    if (tool === 'rectangle' && currentRect) {
      const width = coords.x - currentRect.x;
      const height = coords.y - currentRect.y;
      
      // Redraw canvas with current rectangle
      drawCanvas(img, currentPage);
      
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';  // Semi-transparent while drawing
      const rotatedCoords = rotateCoordinates(currentRect.x, currentRect.y, width, height, img.width, img.height, rotation);
      ctx.fillRect(rotatedCoords.x, rotatedCoords.y, rotatedCoords.width, rotatedCoords.height);
      
      setCurrentRect({ ...currentRect, width, height });
    } else if (tool === 'brush') {
      setCurrentStroke(prev => [...prev, coords]);
      
      // Draw the new segment
      const ctx = canvasRef.current.getContext('2d');
      const prevPoint = currentStroke[currentStroke.length - 1] || coords;
      const rotatedPrev = rotatePoint(prevPoint.x, prevPoint.y, img.width, img.height, rotation);
      const rotatedCurr = rotatePoint(coords.x, coords.y, img.width, img.height, rotation);
      
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 20;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(rotatedPrev.x, rotatedPrev.y);
      ctx.lineTo(rotatedCurr.x, rotatedCurr.y);
      ctx.stroke();
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    const pageKey = String(currentPage);
    const pageRed = redactionsByPage[pageKey] || { rectangles: [], brushStrokes: [] };

    if (tool === 'rectangle' && currentRect && (Math.abs(currentRect.width) > 5 || Math.abs(currentRect.height) > 5)) {
      setRedactionsByPage({
        ...redactionsByPage,
        [pageKey]: {
          ...pageRed,
          rectangles: [...pageRed.rectangles, currentRect]
        }
      });
      setCurrentRect(null);
    } else if (tool === 'brush' && currentStroke.length > 1) {
      setRedactionsByPage({
        ...redactionsByPage,
        [pageKey]: {
          ...pageRed,
          brushStrokes: [...pageRed.brushStrokes, currentStroke]
        }
      });
      setCurrentStroke([]);
    }
  };

  const handleUndo = () => {
    const pageKey = String(currentPage);
    const pageRed = redactionsByPage[pageKey];
    
    if (!pageRed) return;
    
    if (pageRed.rectangles && pageRed.rectangles.length > 0) {
      setRedactionsByPage({
        ...redactionsByPage,
        [pageKey]: {
          ...pageRed,
          rectangles: pageRed.rectangles.slice(0, -1)
        }
      });
    } else if (pageRed.brushStrokes && pageRed.brushStrokes.length > 0) {
      setRedactionsByPage({
        ...redactionsByPage,
        [pageKey]: {
          ...pageRed,
          brushStrokes: pageRed.brushStrokes.slice(0, -1)
        }
      });
    }
    
    // Redraw
    if (images[currentPage]) {
      setTimeout(() => drawCanvas(images[currentPage], currentPage), 0);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      
      // Load page if not already loaded
      if (!images[newPage]) {
        loadPageIfNeeded(newPage).then(img => {
          if (img) drawCanvas(img, newPage);
        });
      } else {
        drawCanvas(images[newPage], newPage);
      }
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      
      // Page should already be loaded since we navigated forward before
      if (images[newPage]) {
        drawCanvas(images[newPage], newPage);
      } else {
        loadPageIfNeeded(newPage).then(img => {
          if (img) drawCanvas(img, newPage);
        });
      }
    }
  };

  const handleSave = async () => {
    try {
      // Convert redactionsByPage format for backend
      const formattedRedactionsByPage = {};
      for (const [pageNum, pageData] of Object.entries(redactionsByPage)) {
        formattedRedactionsByPage[pageNum] = {
          rectangles: pageData.rectangles.map(r => [r.x, r.y, r.width, r.height]),
          brushStrokes: pageData.brushStrokes || []
        };
      }
      
      await onSave(formattedRedactionsByPage);
      
      const totalPages = Object.keys(formattedRedactionsByPage).length;
      toast.success(`Redactions saved successfully on ${totalPages} page(s)`);
      onClose();
    } catch (error) {
      toast.error('Failed to save redactions');
      console.error(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Manual Redaction - {fileName}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col space-y-4">
          {/* Toolbar */}
          <div className="flex items-center space-x-2 p-2 bg-slate-100 dark:bg-slate-800 rounded flex-wrap gap-2">
            <div className="flex items-center space-x-2 border-r pr-2">
              <Button
                size="sm"
                variant={tool === 'rectangle' ? 'default' : 'outline'}
                onClick={() => setTool('rectangle')}
              >
                <Square className="h-4 w-4 mr-1" />
                Box
              </Button>
              <Button
                size="sm"
                variant={tool === 'brush' ? 'default' : 'outline'}
                onClick={() => setTool('brush')}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Brush
              </Button>
            </div>

            <div className="flex items-center space-x-2 border-r pr-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs font-medium min-w-[3rem] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center space-x-2 border-r pr-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRotateLeft}
                title="Rotate Left"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <span className="text-xs font-medium min-w-[2rem] text-center">
                {rotation}°
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRotateRight}
                title="Rotate Right"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1" />
            
            <Button
              size="sm"
              variant="outline"
              onClick={handleUndo}
              disabled={!redactionsByPage[String(currentPage)] || 
                (redactionsByPage[String(currentPage)].rectangles.length === 0 && 
                 redactionsByPage[String(currentPage)].brushStrokes.length === 0)}
            >
              <Undo className="h-4 w-4 mr-1" />
              Undo
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={Object.keys(redactionsByPage).length === 0}
            >
              <Save className="h-4 w-4 mr-1" />
              Save
            </Button>
          </div>

          {/* Canvas */}
          <div 
            ref={containerRef}
            className="flex-1 overflow-auto border rounded bg-slate-50 dark:bg-slate-900 p-4"
          >
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-slate-500">Loading file...</p>
              </div>
            ) : imageLoaded ? (
              <div style={{ 
                transform: `scale(${zoom})`, 
                transformOrigin: 'top left',
                display: 'inline-block'
              }}>
                <canvas
                  ref={canvasRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  className="cursor-crosshair border border-slate-300 dark:border-slate-600"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-slate-500">Click a file to start redacting</p>
              </div>
            )}
          </div>

          {/* Page Navigation */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-4 p-2 bg-slate-100 dark:bg-slate-800 rounded">
              <Button
                size="sm"
                variant="outline"
                onClick={handlePrevPage}
                disabled={currentPage === 0}
              >
                Previous
              </Button>
              <span className="text-sm font-medium">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          )}

          <p className="text-xs text-slate-500 dark:text-slate-400">
            {tool === 'rectangle' ? 
              'Click and drag to draw black rectangles over sensitive information. ' :
              'Click and drag to draw freehand over sensitive information. '
            }
            Use Ctrl+Scroll to zoom. Rotate if document is sideways.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
